/**
 * Payment History Table with Tabulator - Remote Pagination
 *
 * @package WPCore
 */

import { initCustomPagination } from '../tabulator/custom-pagination.js';

/**
 * Build AJAX parameters for Tabulator remote pagination
 * @returns {Function} Function that returns params
 */
function buildAjaxParams() {
	return () => ({
		action: 'wpcore_payment_history_pagination',
		nonce: wpcore.nonce
	});
}

/**
 * Download invoice PDF from Stripe via AJAX
 * @param {string} invoiceId - Stripe invoice ID (e.g. in_xxxxx)
 */
async function downloadInvoicePdf(invoiceId) {
	if (!invoiceId) {
		wpcore.alert?.('error', 'Download', wpcore.direct_t({ en: 'Invoice PDF not available.', fr: 'PDF de facture non disponible.' }));
		return;
	}

	const formData = new FormData();
	formData.append('action', 'wpcore_get_invoice_pdf');
	formData.append('nonce', wpcore.nonce);
	formData.append('invoice_id', invoiceId);

	try {
		const res = await fetch(wpcore.ajaxUrl, { method: 'POST', body: formData });
		const json = await res.json();

		if (json.success && json.data.pdf_url) {
			window.open(json.data.pdf_url, '_blank');
		} else {
			wpcore.alert?.('error', 'Download', wpcore.direct_t({ en: 'Invoice PDF not available.', fr: 'PDF de facture non disponible.' }));
		}
	} catch (error) {
		console.error('[Invoice Download]', error);
		wpcore.alert?.('error', 'Download', wpcore.direct_t({ en: 'Failed to download invoice.', fr: 'Échec du téléchargement de la facture.' }));
	}
}

export async function initPaymentHistoryTable() {
	// Expose globally for mobile onclick
	window.downloadInvoicePdf = downloadInvoicePdf;

	const tableContainer = document.getElementById('payment-history-table');
	if (!tableContainer) return;

	// eslint-disable-next-line no-undef
	const table = new Tabulator('#payment-history-table', {
		// Remote pagination config
		ajaxURL: wpcore.ajaxUrl,
		ajaxConfig: 'POST',
		ajaxContentType: {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded'
			},
			body: function (_url, _config, params) {
				return new URLSearchParams(params).toString();
			}
		},
		ajaxParams: buildAjaxParams(),
		// Loading indicator
		dataLoader: true,
		dataLoaderLoading: '<div class="tabulator-loading-overlay"><div class="tabulator-loader"></div></div>',
		dataLoaderError: '<div class="tabulator-loading-overlay"><div class="text-red-500">Error loading data</div></div>',
		// Pagination config - remote mode
		paginationMode: 'remote',
		pagination: true,
		paginationSize: 10,
		placeholder: 'No payment history',
		layout: 'fitColumns',
		columns: [
			{
				field: 'mobile_html',
				formatter: 'html',
				headerSort: false,
				widthGrow: 1,
				visible: window.innerWidth < 768,
				cssClass: '!p-0 !border-none',
			},
			{
				title: 'Invoice ID',
				field: 'invoiceDisplayId',
				minWidth: 140,
				widthGrow: 1,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				formatter: 'html',
				visible: window.innerWidth >= 768,
			},
			{
				title: 'Date',
				field: 'date',
				minWidth: 120,
				widthGrow: 1.5,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				visible: window.innerWidth >= 768,
			},
			{
				title: 'Plan',
				field: 'plan',
				minWidth: 120,
				widthGrow: 1.5,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				formatter: 'html',
				visible: window.innerWidth >= 768,
			},
			{
				title: 'Amount',
				field: 'amount',
				minWidth: 100,
				widthGrow: 1,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				visible: window.innerWidth >= 768,
			},
			{
				title: 'Status',
				field: 'status',
				minWidth: 100,
				widthGrow: 1,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				formatter: 'html',
				visible: window.innerWidth >= 768,
			},
			{
				title: '',
				field: 'download',
				width: 50,
				hozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				visible: window.innerWidth >= 768,
				formatter: function () {
					return '<button class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary-600 transition-all cursor-pointer"><span class="fi fi-sr-download text-sm"></span></button>';
				},
				cellClick: function (_e, cell) {
					const invoiceId = cell.getRow().getData().invoiceId;
					downloadInvoicePdf(invoiceId);
				},
			},
		],
	});

	// Toggle columns and header visibility
	const toggleResponsiveView = () => {
		const isMobile = window.innerWidth < 768;
		const header = tableContainer.querySelector('.tabulator-header');

		if (isMobile) {
			table.showColumn('mobile_html');
			table.hideColumn('invoiceDisplayId');
			table.hideColumn('date');
			table.hideColumn('plan');
			table.hideColumn('amount');
			table.hideColumn('status');
			table.hideColumn('download');
			if (header) header.style.display = 'none';
		} else {
			table.hideColumn('mobile_html');
			table.showColumn('invoiceDisplayId');
			table.showColumn('date');
			table.showColumn('plan');
			table.showColumn('amount');
			table.showColumn('status');
			table.showColumn('download');
			if (header) header.style.display = 'flex';
		}
	};

	table.on('tableBuilt', toggleResponsiveView);
	window.addEventListener('resize', toggleResponsiveView);

	// Initialize custom pagination
	await initCustomPagination(table, {
		tableElement: tableContainer,
		containerId: 'payment-history-pagination'
	});
}